import React from "react";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";

type RpRowHtmlProps = {
  html?: string;
  as?: React.ElementType;
  [key: string]: any;
};

/**
 * Validates iframe src URL to ensure it's from a trusted domain
 * @param {string} url - The iframe src URL
 * @returns {boolean} - True if URL is from a trusted domain
 */
const isValidIframeSrc = (url: string) => {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const urlObj = new URL(url, window.location.origin);
    const hostname = urlObj.hostname.toLowerCase();

    // Allowed domains for iframes
    const allowedDomains = [
      // QuickSight (AWS)
      "quicksight.aws.amazon.com",
      "quicksight.amazonaws.com",
      /^[a-z0-9-]+\.quicksight\.(us|eu|ap|sa|ca|cn)-(central|east|west|north|south)-\d+\.amazonaws\.com$/,
      // YouTube
      "www.youtube.com",
      "youtube.com",
      "www.youtube-nocookie.com",
      "youtube-nocookie.com",
      "youtu.be",
      "www.youtu.be",
      // Vimeo (common video platform)
      "player.vimeo.com",
      "vimeo.com",
      // Add more trusted domains as needed
    ];

    return allowedDomains.some((domain) => {
      if (typeof domain === "string") {
        return hostname === domain || hostname.endsWith(`.${domain}`);
      }
      if (domain instanceof RegExp) {
        return domain.test(hostname);
      }
      return false;
    });
  } catch (error) {
    // Invalid URL format
    return false;
  }
};

/**
 * Component to sanitize and render HTML with secure DOMPurify configuration
 * Supports editor content (CKEditor-like) and iframe embeds
 * Iframes are validated to only allow trusted domains (QuickSight, YouTube, etc.)
 */
function RpRowHtml({ html, as: Component = "div", ...props }: RpRowHtmlProps) {
  // Input validation
  if (!html || typeof html !== "string") {
    return <Component {...props} />;
  }

  // Sanitize HTML with secure configuration
  const cleanHtml = DOMPurify.sanitize(html, {
    // Use ALLOWED_TAGS instead of ADD_TAGS for better security
    ALLOWED_TAGS: [
      // Text formatting
      "p",
      "br",
      "strong",
      "em",
      "b",
      "i",
      "u",
      "s",
      "strike",
      "sub",
      "sup",
      "span",
      "div",
      // Headings
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      // Lists
      "ul",
      "ol",
      "li",
      "dl",
      "dt",
      "dd",
      // Code
      "code",
      "pre",
      // Links and media
      "a",
      "img",
      // Tables
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "th",
      "td",
      "caption",
      "colgroup",
      "col",
      // Blockquotes
      "blockquote",
      "cite",
      // Horizontal rule
      "hr",
      // Iframe (for embeds - validated separately)
      "iframe",
    ],
    ALLOWED_ATTR: [
      // Common attributes
      "class",
      "id",
      "style",
      "title",
      "alt",
      "role",
      "aria-label",
      "aria-labelledby",
      // Link attributes
      "href",
      "target",
      "rel",
      // Image attributes
      "src",
      "width",
      "height",
      "loading",
      // Table attributes
      "colspan",
      "rowspan",
      "scope",
      "headers",
      // Iframe attributes (validated separately)
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "sandbox",
      "referrerpolicy",
    ],
    ALLOW_DATA_ATTR: false, // Disable data attributes for security
    // Explicitly forbid dangerous tags
    FORBID_TAGS: [
      "script",
      "object",
      "embed",
      "form",
      "input",
      "button",
      "select",
      "textarea",
      "option",
      "meta",
      "link",
      "style",
      "base",
    ],
    // Explicitly forbid dangerous attributes
    FORBID_ATTR: [
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onmouseout",
      "onfocus",
      "onblur",
      "onchange",
      "onsubmit",
      "onreset",
      "onselect",
      "onkeydown",
      "onkeypress",
      "onkeyup",
    ],
    // Validate iframe sources
    ADD_URI_SAFE_ATTR: ["href", "src"],
    // Custom hook to validate iframe src
    SAFE_FOR_TEMPLATES: false,
  });

  // Additional validation for iframes after sanitization
  // Check if any iframes remain and validate their src
  const iframeRegex = /<iframe[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;
  let finalHtml = cleanHtml;

  while ((match = iframeRegex.exec(cleanHtml)) !== null) {
    const iframeSrc = match[1];
    if (!isValidIframeSrc(iframeSrc)) {
      // Remove iframe if src is not from trusted domain
      finalHtml = finalHtml.replace(match[0], "");
    }
  }

  const HtmlComponent = Component as React.ElementType<any>;

  return (
    <HtmlComponent dangerouslySetInnerHTML={{ __html: finalHtml }} {...props} />
  );
}

export default RpRowHtml;

RpRowHtml.propTypes = {
  html: PropTypes.string,
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
};

RpRowHtml.defaultProps = {
  html: "",
  as: "div",
};
